import React from 'react';

import { translate } from '~l10n/client/translate';
import { IconCheckmark } from '~shared/components/icons/micro';
import { TooltipV2 } from '~shared/components/TooltipV2/TooltipV2';

interface SpecificStudentArea {
  index: number;
  name: string;
  description: string;
  isImprovementNeeded: boolean;
}

interface SpecificStudentAreasProps {
  areas: SpecificStudentArea[];
}

export function SpecificStudentAreas({ areas }: SpecificStudentAreasProps) {
  // Create array of 5 slots, filling with areas from the left
  const slots = Array(5).fill(null);
  areas.forEach((area, idx) => {
    slots[idx] = area;
  });

  return (
    <div className="flex items-center gap-1 w-48">
      {slots.map((area, idx) =>
        area ? (
          <AreaItem key={area.index} area={area} />
        ) : (
          <div key={`empty-${idx}`} className="w-8 h-8" />
        )
      )}
    </div>
  );
}

function AreaItem({ area }: { area: SpecificStudentArea }) {
  const { index, isImprovementNeeded, name } = area;

  const tooltipText = isImprovementNeeded
    ? translate('Needs improvement in {0}. {1}').format(index + 1, name)
    : translate('No improvement needed in {0}. {1}').format(index + 1, name);

  if (isImprovementNeeded) {
    // Active state: Red circle with number
    return (
      <TooltipV2
        label={tooltipText}
        delay={0}
        mode="click"
        contentWidth={260}
        direction="top"
      >
        <div className="flex items-center justify-center w-8 h-8 rounded-round border border-solid border-red-200 bg-white text-xs font-600 text-red-800 cursor-pointer transition-opacity hover:opacity-50">
          {translate('{0}').format(index + 1)}
        </div>
      </TooltipV2>
    );
  }

  // Inactive state: Number with checkmark
  return (
    <TooltipV2
      label={tooltipText}
      delay={0}
      mode="click"
      contentWidth={260}
      direction="top"
    >
      <div className="flex flex-col items-center justify-center mb-3 w-8 h-10 text-xs text-blue-600 cursor-pointer transition-opacity hover:opacity-50">
        <IconCheckmark
          size="micro"
          titleId="AreasOfImprovementCorrect"
          title="Areas of Improvement Correct"
        />
        <span className="-mt-1">{translate('{0}').format(index + 1)}</span>
      </div>
    </TooltipV2>
  );
}
